import clsx from 'clsx'
import {cloneDeep} from 'lodash'
import React from 'react'
import {useIntl} from 'react-intl'
import {KTSVG} from '../../../../../_gori/helpers'
import {Button, SelectFormik} from '../../../../../_gori/partials/widgets'
import {
  FEDEX_INTERNATIONAL_SERVICES,
  FEDEX_PARCEL_LIMIT,
  OPTION_PACKAGE_TYPE,
  UNIT_VALUES,
} from '../../core/_const'
import {PackageType} from './PackageType'
import {
  OPTION_DIMENSION_UNIT,
  OPTION_WEIGHT_UNIT,
  TABLET_BREAKPOINT,
} from '../../../../../_gori/constants'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import useWindowDimensions from '../../../../../_gori/hooks/useWindowDimensions'
import {usePackageInfo} from './usePackageInfo'
import {PackageInfoRow} from './PackageInfoRow'

type Props = {
  formik: any
  disabled?: boolean
  className?: string
  hasGetPackage?: boolean
  handleAddNewItem?: (section: string) => void
  handleDeleteItem?: (section: string, key: string) => void
  currentDimensionUnit?: string
  setCurrentDimensionUnit?: (unit: string) => void
  currentWeightUnit?: string
  setCurrentWeightUnit?: (unit: string) => void
  isDisabledAddPackage?: boolean
  isInModal?: boolean
}

const PackageInfo: React.FC<Props> = ({
  formik,
  className = '',
  disabled,
  hasGetPackage = true,
  handleAddNewItem,
  handleDeleteItem,
  setCurrentDimensionUnit,
  currentWeightUnit,
  setCurrentWeightUnit,
  isDisabledAddPackage = false,
  isInModal = false,
}) => {
  const intl = useIntl()
  const {width} = useWindowDimensions()
  const isMobileLayout = width < TABLET_BREAKPOINT

  const {
    labelPackageType,
    setLabelPackageType,
    oneRateUnmatched,
    isDisabledPackageCount,
    isDisabledDimension,
    reachLimitRow,
    parcelsCount,
    carriersPackage,
    packageTypePlaceholder,
    optionsPackageTypes,
    optionsCustomPackage,
    totalWeightPackage,
    totalPackages,
    setOneRateUnmatched,
    addPackage,
    removePackage,
    onChangeDimensionUnit,
    onChangeWeightUnit,
    onDimensionChange,
    customSelectInputStyle,
  } = usePackageInfo({
    formik,
    disabled,
    hasGetPackage,
    handleAddNewItem,
    handleDeleteItem,
    setCurrentDimensionUnit,
    setCurrentWeightUnit,
    isDisabledAddPackage,
  })

  const parcelEntries = Object.entries(formik.values.parcels)

  return (
    <div className={clsx({[className]: !!className})}>
      <div>
        {/* Header: title + package type + add button */}
        <div className='d-flex flex-wrap gap-2 justify-content-between align-items-center mt-10 mb-3'>
          <div className='fs-3 fw-bolder'>{intl.formatMessage({id: 'PACKAGE_INFO'})}</div>
        </div>

        <div className='d-flex flex-wrap flex-column-reverse flex-sm-row gap-2 justify-content-between align-items-start align-items-sm-center mb-7'>
          <div className='d-flex col-12 col-sm-6 pe-sm-5'>
            <PackageType
              data={[
                OPTION_PACKAGE_TYPE.INPUT_DIMENSION,
                ...cloneDeep(optionsPackageTypes),
                optionsCustomPackage,
              ]}
              className='w-100'
              labelClassName='col-form-label mb-0 fw-bolder'
              formik={formik}
              name='parcels.0.package_type'
              placeholder={packageTypePlaceholder}
              disabled={disabled || parcelsCount > 1}
              labelPackageType={labelPackageType}
              setLabelPackageType={setLabelPackageType}
              oneRateUnmatched={oneRateUnmatched}
              onResetOneRateUnmatched={() => setOneRateUnmatched(false)}
            />
          </div>
          <Button
            className={clsx(
              'd-flex align-items-center fw-bolder cursor-pointer btn btn-advanced-tertiary px-1 py-0 fs-inherit',
              {'pe-none ': disabled || reachLimitRow || isDisabledAddPackage}
            )}
            event={() => !(isDisabledAddPackage || disabled || reachLimitRow) && addPackage()}
            disabled={disabled || reachLimitRow || isDisabledAddPackage}
          >
            <span className='fs-2 me-1'>+</span>
            {intl.formatMessage({id: 'ADD_PACKAGE'})}
          </Button>
        </div>

        <div className='d-flex row package-list bg-light rounded py-3 mx-0'>
          {!isMobileLayout && (
            <div className='d-flex justify-content-center align-items-center mb-1'>
              <div className='col-12 d-flex flex-wrap package-labels'>
                <div className='col-6 d-flex justify-content-between align-items-center pe-5'>
                  <div className={clsx('fw-bold form-label mb-0 ps-2', {'fs-7': isInModal})}>
                    {intl.formatMessage({id: 'DIMENSIONS'})}{' '}
                    <span className='fw-light'>{'L*W*H'}</span>
                  </div>
                  <SelectFormik
                    className={clsx('dimension-unit-select', {
                      'pe-none': disabled,
                      'fs-7': isInModal,
                    })}
                    placeholder=''
                    label=''
                    labelClassName='text-muted'
                    options={OPTION_DIMENSION_UNIT}
                    name='dimension_unit'
                    formik={formik}
                    disabled={isDisabledDimension}
                    emptyDefault={false}
                    onChange={(e) => onChangeDimensionUnit(e)}
                    hideIndicatorSeparator
                    isSearchable={false}
                    {...customSelectInputStyle(isDisabledDimension)}
                  />
                </div>
                <div className='col-6 d-flex justify-content-between align-items-center'>
                  <div className='col-6 d-flex justify-content-between align-items-center pe-2'>
                    <div
                      className={clsx('fw-bold form-label mb-0', {
                        'fs-7': isInModal,
                      })}
                    >
                      {intl.formatMessage({id: 'WEIGHT'})}
                    </div>
                    <SelectFormik
                      className={clsx('weight-unit-select', {
                        'pe-none': disabled,
                        'fs-7': isInModal,
                      })}
                      label=''
                      placeholder=''
                      labelClassName='text-muted bg-light'
                      options={OPTION_WEIGHT_UNIT}
                      name='weight_unit'
                      formik={formik}
                      disabled={disabled}
                      emptyDefault={false}
                      onChange={(e) => onChangeWeightUnit(e)}
                      hideIndicatorSeparator
                      isSearchable={false}
                      {...customSelectInputStyle(disabled)}
                    />
                  </div>
                  <div className='col-6 ps-2'>
                    <div
                      className={clsx(
                        'fw-bold form-label mb-0 d-flex justify-content-start align-items-center',
                        {'fs-7': isInModal}
                      )}
                    >
                      {intl.formatMessage({id: 'PACKAGE_COUNT'})}
                      <OverlayTrigger
                        overlay={
                          <Tooltip id='tooltip-disabled'>
                            {intl.formatMessage({id: 'PACKAGE_COUNT_TOOLTIP'})}
                          </Tooltip>
                        }
                      >
                        <span className='d-flex justify-content-start align-items-start'>
                          <KTSVG
                            path='/media/gori/orders/info.svg'
                            className='ms-1 mb-1 svg-icon-4 cursor-pointer'
                          />
                        </span>
                      </OverlayTrigger>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {parcelEntries.map(([keyItem], index) => (
            <PackageInfoRow
              key={keyItem}
              formik={formik}
              keyItem={keyItem}
              index={index}
              disabled={disabled}
              isDisabledDimension={isDisabledDimension}
              isDisabledPackageCount={isDisabledPackageCount}
              oneRateUnmatched={oneRateUnmatched}
              carriersPackage={carriersPackage}
              displayExtendParts={parcelEntries.length === 1 && index === 0}
              disabledDelete={parcelEntries.length < 2 || !!disabled}
              isMobileLayout={isMobileLayout}
              isInModal={isInModal}
              onDimensionChange={onDimensionChange}
              onChangeDimensionUnit={onChangeDimensionUnit}
              onChangeWeightUnit={onChangeWeightUnit}
              onRemove={removePackage}
              customSelectInputStyle={customSelectInputStyle}
            />
          ))}
        </div>

        {oneRateUnmatched && (
          <div className='text-danger d-flex align-items-center mt-2 ps-2 fs-7'>
            <KTSVG path='/media/gori/settings/errors.svg' className='me-2 svg-icon-4' />
            {intl.formatMessage({id: 'ONE_RATE_UNMATCHED'})}
          </div>
        )}

        <div className='row'>
          <div className='col-12 d-flex flex-wrap'>
            <div className='col-12 col-sm-6 d-flex flex-column justify-content-center align-items-start'>
              <div className='d-flex align-items-center text-muted mt-3'>
                {intl.formatMessage({id: 'TOTAL_WEIGHT'})}:
                <input name='check_weight' type='hidden' />
                <span className='text-dark fs-1 mx-2'>{totalWeightPackage}</span>{' '}
                {currentWeightUnit?.toUpperCase()}
              </div>
              {formik.errors?.check_weight && (
                <div className='text-danger d-flex justify-content-end ps-0'>
                  {formik.errors?.check_weight}
                </div>
              )}
            </div>
            <div className='col-12 col-sm-6 d-flex flex-column justify-content-center align-items-start align-items-sm-end'>
              <div className='d-flex align-items-center text-muted mt-3'>
                {intl.formatMessage({id: 'TOTAL_PACKAGES'})}:
                <input name='check_weight' type='hidden' />
                <span className='text-dark fs-1 mx-2'>{totalPackages}</span>
              </div>
              {formik.errors?.check_package_count && (
                <div className='text-danger d-flex justify-content-end ps-0'>
                  {intl.formatMessage(
                    {id: 'TOTAL_OF_INPUT_MUST_NOT_BE_GREATER_THAN_NUMBER'},
                    {
                      input: intl.formatMessage({id: 'PACKAGE_COUNT'}),
                      max: FEDEX_INTERNATIONAL_SERVICES.includes(formik.values.service)
                        ? FEDEX_PARCEL_LIMIT
                        : UNIT_VALUES.PACKAGE_COUNT.max,
                    }
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export {PackageInfo}
